import { TubeProperties, Modification } from '../../utils/StandardMTOEnum'
import {
  SingleTubeFormValues,
  ProductInputType,
  checkForRedesignedBiTubeProduct,
} from './ProductsConfiguration'
import {
  TubeTotalValidationResponse,
  SessionInput,
} from '@src/types/graphql-types'
import { getSessionDataFromFormikData } from './SessionDataUtil'
import { FormikErrors } from 'formik'

export const joinMessagesIntoOne = (errors: string[]): string =>
  errors.sort().join('#%')
export const splitMessages = (error: string): string[] => error.split('#%')

export const TradeMarkURL =
  '/life-science/legal/product-licenses/oligonucleotide-label-licenses'

export const hasNoErrors = (data: any) => {
  if (!data) return false
  return Object.values(data).every((value) => value === '')
}

export const truncateLongString = (name: string, maxLength: number) => {
  return name?.length > maxLength ? name.substring(0, maxLength) + '...' : name
}

export const getDisplayName = (inputParam: string | string[] = '') => {
  const inputValue = inputParam?.toString()

  if (inputValue?.toLowerCase().endsWith('umo')) {
    const numericPart = parseFloat(inputValue.split(' ')[0]).toFixed(4)
    return `_PC_SPEC_${numericPart.replace('.', '_')}_UMO`
  }

  if (inputValue?.toLowerCase().endsWith('um')) {
    return `_PC_SPEC_${inputValue.toUpperCase()}`
  }

  const specialCases = {
    DESALT: '_PC_SPEC_DESALT',
    RP1: '_PC_SPEC_CARTRIDGE',
    VIVO: '_PC_SPEC_VIVO_V2',
    HPLC: '_PC_SPEC_HPLC',
    PAGE: '_PC_SPEC_PAGE',
    DRY: '_PC_SPEC_DRY',
    LIQUID: '_PC_SPEC_IS_WATER',
    TE: '_PC_SPEC_IS_TE',
    TRIS: '_PC_SPEC_IS_TRIS',
    ['[None]']: '_PC_SECONDARY_PATTERN_NONE',
  }

  if (inputValue in specialCases) {
    return specialCases[inputValue]
  }

  return inputValue
}

export const updateOligoDataWithPrice = (
  originalData: SingleTubeFormValues[],
  updateData: SingleTubeFormValues[]
) => {
  // NOTE: for DNA tubes only valid data is sent to price call as payload,
  //  but bitubes pull data from session and response might have data with no price due to errors
  const dataWithPrice = updateData.filter(
    (data) => data.listErrors.length === 0
  )

  return originalData.map((originalItem, index) => {
    const updateItem = dataWithPrice[index]

    if (!updateItem) {
      return originalItem
    }

    return {
      ...originalItem,
      price: updateItem.price,
      epsilon: updateItem.epsilon,
      currencySymbol: updateItem.currencySymbol,
      currency: updateItem.currency,
      material: updateItem.material,
      brand: updateItem.brand,
    }
  })
}

export const parseFloatPriceToNumber = (
  value: string | number | null | undefined
): number => {
  if (typeof value === 'number') {
    return value > 0 && Number.isFinite(value) ? value : 0
  }

  if (!value) {
    return 0
  }
  const num = parseFloat(value.toString())
  return num > 0 && Number.isFinite(num) ? num : 0
}

interface CheckModAndScrollProps {
  validationObj: FormikErrors<SingleTubeFormValues> | undefined
  callback?: (bool: boolean) => void
  isBiTube?: boolean
}
//this function checks whether we should open the accordion and / or scroll the user to specific section based on which section the error(s) are
export const checkModErrorsAndScroll = ({
  validationObj,
  callback,
  isBiTube = false,
}: CheckModAndScrollProps) => {
  if (!validationObj) return
  const errorFields = Object.entries(validationObj).filter(
    ([key, value]) => key && value !== ''
  )

  if (errorFields.length === 0) return

  const hasOnlyModErrors = errorFields.every(
    ([key]) =>
      key === Modification.ThreeMod ||
      key === Modification.FiveMod ||
      key === 'modification'
  )
  const hasAtLeastOneModError = errorFields.some(
    ([key]) =>
      key === Modification.ThreeMod ||
      key === Modification.FiveMod ||
      key === 'modification'
  )

  const hasNameOrSequenceOrOverHangeError = errorFields.some(
    ([key]) =>
      key === TubeProperties.Sequence ||
      key === TubeProperties.Name ||
      key.toLowerCase() === TubeProperties.CustomerOverhang.toLowerCase()
  )
  const hasOnlySpecificationErrors = errorFields.every(
    ([key]) =>
      key === TubeProperties.Concentration ||
      key === TubeProperties.Format ||
      key === TubeProperties.Purification ||
      key === TubeProperties.Scale ||
      key === TubeProperties.Amount
  )
  const hasSomeSpecificationErrors = errorFields.some(
    ([key]) =>
      key === TubeProperties.Concentration ||
      key === TubeProperties.Format ||
      key === TubeProperties.Purification ||
      key === TubeProperties.Scale ||
      key === TubeProperties.Amount
  )
  if (hasNameOrSequenceOrOverHangeError) [window.scrollTo(0, 200)]

  const hasAntiSenseNameOrSequenceorOverHangeError = errorFields.some(
    ([key]) =>
      key.toLowerCase() === TubeProperties.AntiSenseSequence.toLowerCase() ||
      key.toLowerCase() === TubeProperties.AntiSenseName.toLowerCase() ||
      key.toLowerCase() ===
        TubeProperties.CustomerAntisenseOverHang.toLowerCase()
  )

  if (hasAntiSenseNameOrSequenceorOverHangeError) {
    window.scrollTo(0, 400)
  }

  if (hasOnlyModErrors) {
    callback && callback(true)
    setTimeout(() => {
      window.scrollTo(0, 800)
    }, 1000)
  }
  if (hasOnlySpecificationErrors) {
    setTimeout(() => {
      window.scrollTo(0, isBiTube ? 1600 : 1100)
    }, 1000)
  }
  if (hasAtLeastOneModError && hasNameOrSequenceOrOverHangeError) {
    callback && callback(true)
  }
  if (
    hasSomeSpecificationErrors &&
    hasAtLeastOneModError &&
    !hasNameOrSequenceOrOverHangeError
  ) {
    callback && callback(true)
    setTimeout(() => {
      window.scrollTo(0, 800)
    }, 1000)
  }
}

export const updatedData = (oligoList: any[], validationData: any) => {
  if (
    !validationData ||
    !Array.isArray(validationData) ||
    validationData.length === 0
  ) {
    return oligoList
  }

  return oligoList.map((oligo, index) => {
    const matchingValidation = validationData.find(
      (v) => Number(v.oligoId) === index
    )
    return matchingValidation
      ? {
          ...oligo,
          listErrors:
            matchingValidation.listErrors?.filter(
              (error) => error?.key !== 'ReviewError'
            ) || [],
          displayPageError: matchingValidation?.listErrors?.length > 0,
        }
      : oligo
  })
}

export const transformString = (inputString: string): string => {
  const lowercaseString = inputString.toLowerCase()

  const stringWithoutUnderscores = lowercaseString.replace(/_/g, '')

  return stringWithoutUnderscores.replace(/ed$/, '')
}

export const updatedDataWithPrice = (
  oligoList: SingleTubeFormValues[],
  validationData: TubeTotalValidationResponse[],
  priceData: SingleTubeFormValues[]
): any[] => {
  if (
    !validationData ||
    !Array.isArray(validationData) ||
    validationData.length === 0
  ) {
    return oligoList
  }
  return oligoList.map((oligo, index) => {
    const itemPriceData = priceData.find((price) => price.oligoId === index)
    const itemValidationData = validationData.find(
      (v) => Number(v.oligoId) === index
    )
    const errorList =
      itemValidationData?.listErrors?.filter(
        (error) => error?.key !== 'ReviewError'
      ) || []
    return {
      ...oligo,
      ...(itemValidationData && {
        listErrors: errorList,
        displayPageError: itemValidationData?.listErrors?.length !== 0,
      }),
      ...(itemPriceData && {
        price: itemPriceData.price,
        epsilon: itemPriceData.epsilon,
        currencySymbol: itemPriceData.currencySymbol,
        currency: itemPriceData.currency,
        material: itemPriceData.material,
        brand: itemPriceData.brand,
      }),
    }
  })
}

export const handleOligoValidationAndPricing = async (
  product: string,
  newList: SingleTubeFormValues[],
  formik: any,
  productDetails: ProductInputType,
  validationCall: (v: SessionInput[]) => Promise<TubeTotalValidationResponse[]>,
  priceCall: any,
  applyToNextOligos = false
) => {
  const currentSessionData = getSessionDataFromFormikData(product, newList)

  const tubeTotalValidationData = await validationCall(currentSessionData)

  const validOligoIdList = tubeTotalValidationData
    ?.filter((data) => data?.listErrors?.length === 0)
    .map((data) => data.oligoId)

  const validList = newList.filter((_, index) =>
    validOligoIdList?.includes(index)
  )

  let priceResults: any = []

  if (validList.length > 0) {
    const sessionDataForPriceCall = getSessionDataFromFormikData(
      product,
      validList,
      false,
      true
    )
    const priceCallResponseData: any = checkForRedesignedBiTubeProduct(product)
      ? await priceCall({
          variables: { product: productDetails },
        })
      : await priceCall({
          variables: {
            input: sessionDataForPriceCall,
            productInput: productDetails,
          },
        })

    priceResults = checkForRedesignedBiTubeProduct(product)
      ? priceCallResponseData?.data?.biTubePriceCall || []
      : priceCallResponseData?.data?.tubePriceCall?.result || []
  }
  formik.setValues({
    ...formik.allValues,
    applyToNextOligos: applyToNextOligos,
    oligoList: updatedDataWithPrice(
      newList,
      tubeTotalValidationData,
      priceResults
    ),
  })
}
